import { Box, Typography } from '@mui/material';
import React from 'react';

type SprintHeatTableProps = {
    riders: string[][];
    heat1TimeIndex: number;
    heat2TimeIndex: number;
    deciderTimeIndex: number;
    countryColIndex: number;
    getFlagUrl: (country: string) => string;
};

export const SprintMobile = ({
    riders,
    heat1TimeIndex,
    heat2TimeIndex,
    deciderTimeIndex,
    countryColIndex,
    getFlagUrl,
}: SprintHeatTableProps) => {
    // ✅ detect which columns actually exist
    const visibleColumns = {
        heat1: riders.some((r) => r?.[heat1TimeIndex]),
        heat2: riders.some((r) => r?.[heat2TimeIndex]),
        decider: riders.some((r) => r?.[deciderTimeIndex]),
    };

    // ✅ build dynamic grid columns
    const columns = [visibleColumns.heat1 && '1fr', visibleColumns.heat2 && '1fr', visibleColumns.decider && '1fr']
        .filter(Boolean)
        .join(' ');

    const gridMinWidth =
        (visibleColumns.heat1 ? 63 : 0) + (visibleColumns.heat2 ? 63 : 0) + (visibleColumns.decider ? 63 : 0);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {riders.map((rider, i) => {
                if (!rider) return null;

                const rank = rider[0];
                const bib = rider[1];
                const country = rider[countryColIndex];

                const heat1 = rider[heat1TimeIndex];
                const heat2 = rider[heat2TimeIndex];
                const decider = rider[deciderTimeIndex];

                const isH1Winner = rider[5] === 'WINNER';
                const isH2Winner = rider[7] === 'WINNER';
                const isDWinner = rider[9] === 'WINNER';

                return (
                    <Box
                        key={i}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 0.75,
                            borderRadius: 2,
                        }}
                    >
                        {/* LEFT: rank + name + flag */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                flex: 1,
                                minWidth: 0, // 🔥 prevents overflow issues
                            }}
                        >
                            {/* Rank */}
                            <Typography
                                fontWeight={700}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    textAlign: 'center',
                                }}
                            >
                                {rank === 'DNF' ? '' : '# '}
                                {rank}
                                <Typography
                                    sx={{
                                        fontSize: '0.9rem',
                                        lineHeight: 1.1,
                                    }}
                                >
                                    {bib}
                                </Typography>
                            </Typography>

                            {/* Name */}
                            <Box sx={{ flex: 1 }}>
                                <Typography fontWeight={600} sx={{ lineHeight: 1.1 }}>
                                    {rider[2]}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        pl: 1,
                                        color: 'text.secondary',
                                        lineHeight: 1.1,
                                    }}
                                >
                                    {rider[3]}
                                </Typography>
                            </Box>

                            {/* Flag */}
                            {/* FLAG (fixed position!) */}
                            <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', marginRight: '1.5rem' }}>
                                <img
                                    src={getFlagUrl(country)}
                                    alt={country}
                                    style={{ width: '24px', height: '17px' }}
                                />
                            </Box>
                            {/*<img src={getFlagUrl(country)} alt={country} style={{ width: '24px', height: '17px' }} />*/}
                        </Box>

                        {/* RIGHT: Heat grid */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: columns,
                                gap: 1,
                                minWidth: gridMinWidth || 63, // 🔥 fallback keeps alignment
                                justifyContent: 'end',
                                textAlign: 'right',
                            }}
                        >
                            {visibleColumns.heat1 && (
                                <Typography
                                    sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: isH1Winner ? 'success.main' : 'text.primary',
                                        paddingRight: '0.5rem',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        textAlign: 'center',
                                    }}
                                >
                                    {isH1Winner && <div>WINNER</div>}
                                    {heat1 || ''}
                                </Typography>
                            )}

                            {visibleColumns.heat2 && (
                                <Typography
                                    sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: isH2Winner ? 'success.main' : 'text.primary',
                                        paddingRight: visibleColumns.decider ? '0.5rem' : '',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        textAlign: 'center',
                                    }}
                                >
                                    {isH2Winner && <div>WINNER</div>}
                                    {heat2 || ''}
                                </Typography>
                            )}

                            {visibleColumns.decider && (
                                <Typography
                                    sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: isDWinner ? 'success.main' : 'text.primary',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        textAlign: 'center',
                                    }}
                                >
                                    {isDWinner && <div>WINNER</div>}
                                    {decider || ''}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};
